import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Note } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Coach-only journal for a single training day.
 *
 * Only rendered when `canEditCore` is true on the parent (coach/admin or
 * the athlete themselves viewing their own program). The backend strips
 * `coach_note` from athlete responses on `GET`, so athletes never see it
 * even if the client accidentally renders this component.
 *
 * Auto-saves on blur, debounced by keystroke pause. Silent success (no
 * toast per keystroke); the sonner errors surface if the API call fails.
 */
export default function CoachJournal({ programId, dayIdx, initial, onSaved }) {
  const [text, setText] = useState(initial || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText(initial || ""); }, [initial]);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.patch(`/programs/${programId}/days/${dayIdx}`, { coach_note: text || null });
      setDirty(false);
      onSaved?.(text);
    } catch {
      toast.error("Could not save coach note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-xl border border-dashed border-moss/40 bg-sage/10 p-3"
      data-testid={`coach-journal-day-${dayIdx}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-moss">
          <Note size={12} weight="fill" /> Coach's journal · not visible to athlete
        </div>
        {saving && <span className="text-[10px] text-ink-3 italic">Saving…</span>}
      </div>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        onBlur={save}
        placeholder="Session cues, atmosphere, form notes, next-week reminders…"
        className="w-full min-h-[80px] rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink font-sans placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-moss/30 resize-y"
        data-testid={`coach-journal-input-${dayIdx}`}
      />
    </div>
  );
}
